import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { XrayRemoteControlConnectionInfo } from '@bymason/xray-remote-control-viewer';
import { useGetScreenCap } from '@bymason/controller-api-hooks';
import {
  ControllerAlert,
  Button,
  Card,
  CardActions,
  CardContent,
  Link,
  MdScreenRotation,
  MdScreenshot,
  Text,
  CircularProgress,
  Box,
  SxProps,
} from '@bymason-private/legacy-pattern-components';
import { LegacyDetailsList, DetailItem, useToast } from '@bymason-private/controller-shared-widgets';
import { DataUsageWarningModal } from '../data-usage-warning-modal';
import { TimeoutWarningModal } from '../timeout-warning-modal';
import { TimeoutModal } from '../timeout-modal';
import { formatMegabytes, formatMinutesSeconds } from '../utils';
import RemoteControlViewer from './remote-control-viewer';
import { FormFactor } from './types';
import { downloadFile } from '@bymason/utils';
import { Theme } from '@bymason-private/controller-v1-theme-provider';

let durationCounter = 0;

type Orientation = 'portrait' | 'landscape';

interface XRayProps {
  deviceFamily: string;
  deviceId: string;
  deviceName: string;
  open?: boolean;
  onSessionEnd?: () => void;
  onSessionStart?: () => void;
}

const responsiveContentSx: SxProps<Theme> = (theme) => ({
  display: 'flex',
  columnGap: 2,
  flexWrap: 'nowrap',
  [theme.breakpoints.up('lg')]: {
    flexWrap: 'wrap',
  },
});

const contentSx: SxProps<Theme> = (theme) => ({
  flex: `0 1 ${theme.breakpoints.values.md / 2}px`,
  margin: theme.spacing(1, 0, 2, 0),
});

const actionRootSx = {
  display: 'flex',
  flexDirection: 'row',
  gap: 2,
};

const orientationButtonSx: SxProps<Theme> = {
  justifyContent: 'flex-start',
  padding: [0.625, 2, 0.625, 1.5],
};

export function XRayRemoteControl({
  deviceFamily,
  deviceId,
  deviceName,
  onSessionEnd,
  onSessionStart,
  open,
}: XRayProps) {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const [getScreenCap, screenCapBlob, screenCapError, screenCapBusy] = useGetScreenCap(authToken);
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [canvasOrientation, setCanvasOrientation] = useState<Orientation>('portrait');
  const [start, setStart] = useState(false);
  const [error, setError] = useState('');
  const [showDataUsageModal, setShowDataUsageModal] = useState(false);
  const [timeoutRemainingSec, setTimeoutRemainingSec] = useState(0);
  const [resetPendingTimeout, setResetPendingTimeout] = useState<(() => void) | null>(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<XrayRemoteControlConnectionInfo | null>(null);
  const [disconnectedInfo, setDisconnectedInfo] = useState<DetailItem[] | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [started, setStarted] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>();
  const canvasAspectRatio = useRef<string>();

  const formFactor = useMemo<FormFactor>(() => {
    const normFamily = deviceFamily.toUpperCase();

    if (normFamily.startsWith('A4100')) return 'A4100';
    if (normFamily.startsWith('F210')) return 'F210';
    // TODO: upload new D215 asset, but D450 works for now.
    if (normFamily.startsWith('D215')) return 'D450';
    if (normFamily.startsWith('D450')) return 'D450';
    if (normFamily.startsWith('G450')) return 'G450';

    // default
    return 'D450';
  }, [deviceFamily]);

  const isActiveSession = useMemo(() => started && sessionInfo?.status === 'running', [
    sessionInfo?.status,
    started,
  ]);

  const connectionInfoItems = useMemo<DetailItem[]>(() => {
    const duration = formatMinutesSeconds(durationSeconds);
    const dataUsed = formatMegabytes(sessionInfo?.bytesReceived, sessionInfo?.bytesSent);

    return [
      { label: 'Duration', value: duration },
      { label: 'Data used', value: dataUsed },
      { label: 'Device model', value: deviceFamily },
    ];
  }, [sessionInfo?.bytesReceived, sessionInfo?.bytesSent, deviceFamily, durationSeconds]);

  const resetXray = useCallback(
    (includeError = false) => {
      canvasRef.current = undefined;
      setOrientation('portrait');
      setCanvasOrientation('portrait');
      setStart(false);
      setTimeoutRemainingSec(0);
      setHasTimedOut(false);
      setSessionInfo(null);
      setDurationSeconds(0);
      setStarted(false);
      if (onSessionEnd) {
        onSessionEnd();
      }
      if (includeError) setError('');
    },
    [onSessionEnd]
  );

  const handleLaunch = () => {
    setDisconnectedInfo(null);
    resetXray(true);
    setShowDataUsageModal(true);
  };

  const handleOnStart = () => {
    setStarted(true);
    if (onSessionStart) {
      onSessionStart();
    }
  };

  const handleOrientationToggle = () => {
    if (orientation === 'landscape') {
      setOrientation('portrait');
    } else {
      setOrientation('landscape');
    }
  };

  const handleTakeScreenshot = () => {
    getScreenCap({ deviceId });
  };

  const handleEndSession = useCallback(() => {
    setDisconnectedInfo(connectionInfoItems);
    resetXray(true);
  }, [connectionInfoItems, resetXray]);

  const handleDataUsageWarningClose = (proceed = false) => {
    setShowDataUsageModal(false);
    setStart(proceed);
  };

  const handleTimeoutWarningClose = (timeToAddBack: number, proceed = false) => {
    durationCounter += timeToAddBack;
    setTimeoutRemainingSec(0);

    if (proceed) {
      resetPendingTimeout && resetPendingTimeout();
    } else {
      resetXray();
    }
  };

  const handleTimeoutClose = () => {
    resetXray();
  };

  const handleTimeoutWarning = useCallback((remainingSeconds: number, reset: () => void) => {
    setResetPendingTimeout(reset);
    setTimeoutRemainingSec(remainingSeconds);
  }, []);

  const handleConnectionInfo = useCallback((info: XrayRemoteControlConnectionInfo) => {
    setSessionInfo(info);
  }, []);

  const handleError = useCallback(
    (err) => {
      setError(err);
      resetXray();
    },
    [resetXray]
  );

  const handleStop = useCallback(() => {
    resetXray();
  }, [resetXray]);

  const handleTimeout = useCallback(() => {
    setHasTimedOut(true);
  }, []);

  const getCanvasAspectRatio = () => {
    if (canvasRef.current) {
      // the canvas that RFB renders has a style attribute
      // that seems to have a width and height that is driven
      // by the RFB. Reading it for changes in rotation.
      const w = parseInt(canvasRef.current?.style.width);
      const h = parseInt(canvasRef.current?.style.height);
      canvasAspectRatio.current = `${w}:${h}`;

      // defensively coding for missing of width or height in style attribute
      return w && h ? w / h : 1;
    } else {
      canvasRef.current = document.querySelector('canvas') || undefined;
      return 1;
    }
  };

  //
  // handle close
  //
  useEffect(() => {
    if (!open && started) {
      handleEndSession();
    }
  }, [handleEndSession, open, started]);

  //
  // setup timer to update session duration
  //
  useEffect(() => {
    let intervalId = 0;

    if (isActiveSession && !timeoutRemainingSec) {
      setDurationSeconds(durationCounter);

      intervalId = window.setInterval(() => {
        durationCounter++;
        setDurationSeconds(durationCounter);
        setCanvasOrientation(getCanvasAspectRatio() > 1 ? 'landscape' : 'portrait');
      }, 1000);
    }

    return () => {
      window.clearInterval(intervalId);

      // reset
      durationCounter = 0;
    };
  }, [isActiveSession, timeoutRemainingSec]);

  //
  // handle timeout
  //
  useEffect(() => {
    if (hasTimedOut) {
      setStart(false);
    }
  }, [hasTimedOut]);

  useLayoutEffect(() => {
    screenCapBlob &&
      downloadFile({
        source: screenCapBlob,
        fileName: `ScreenCap-${deviceId}-${new Date().toISOString()}.png`,
        type: 'image',
      });
  }, [screenCapBlob, deviceId]);

  useEffect(() => {
    if (screenCapError) {
      setToastMessage(`Couldn't get screenshot: ${screenCapError}`);
      setToastSeverity('error');
    }
  }, [screenCapError, setToastMessage, setToastSeverity]);

  return (
    <Box
      style={!open ? { display: 'none' } : undefined}
      sx={(theme) => ({
        columnGap: 2,
        [theme.breakpoints.up('lg')]: {
          display: 'flex',
        },
      })}
    >
      <Box
        sx={(theme) => ({
          minWidth: theme.breakpoints.values.md / 2,
          flex: `0 0 ${theme.breakpoints.values.md / 2}px`,
        })}
      >
        <Card sx={{ border: 'none' }} variant='outlined'>
          {isActiveSession || sessionInfo || disconnectedInfo ? (
            <CardContent>
              <Text variant='h5' fontWeight={800}>
                Remote-Control session information
              </Text>
              <Box sx={responsiveContentSx}>
                <Box sx={contentSx}>
                  <LegacyDetailsList hideIconsColumn items={disconnectedInfo || connectionInfoItems} />
                </Box>
              </Box>
              {error && (
                <ControllerAlert severity='error' variant='outlined'>
                  {error}
                </ControllerAlert>
              )}
            </CardContent>
          ) : (
            <CardContent>
              <Text variant='h5' fontWeight={800}>
                X-Ray Remote Control
              </Text>
              <Box sx={responsiveContentSx}>
                <Box sx={contentSx}>
                  <Text variant='subtitle1' fontSize='0.875rem'>
                    X-Ray remote control allows you to remotely view, control, and screen-capture live devices
                    in the field. Learn more in our{' '}
                    <Link href='https://docs.bymason.com/how-to/xray' target='_blank'>
                      documentation
                    </Link>
                    .
                  </Text>
                </Box>
              </Box>
              {error && (
                <ControllerAlert severity='error' variant='outlined'>
                  {error}
                </ControllerAlert>
              )}
            </CardContent>
          )}
          <CardActions>
            {isActiveSession ? (
              <div>
                <Box sx={actionRootSx}>
                  <Button
                    sx={{
                      ...orientationButtonSx,
                      '& .MuiButton-startIcon': {
                        transform: orientation === 'landscape' ? 'rotate(-135deg)' : 'rotate(-45deg)',
                        transition: 'all .25s ease-out',
                        transitionProperty: 'transform',
                      },
                    }}
                    color='secondary'
                    disabled={formFactor === 'A4100'}
                    onClick={handleOrientationToggle}
                    startIcon={<MdScreenRotation />}
                    variant='outlined'
                  >
                    Rotate to {orientation === 'landscape' ? 'Portrait' : 'Landscape'}
                  </Button>
                  <Button
                    sx={orientationButtonSx}
                    color='secondary'
                    onClick={handleTakeScreenshot}
                    startIcon={screenCapBusy ? <CircularProgress size={20} /> : <MdScreenshot />}
                    variant='outlined'
                    disabled={screenCapBusy}
                  >
                    Take Screenshot
                  </Button>
                </Box>
                <Box sx={actionRootSx}>
                  <Button
                    sx={{ '& .MuiButton-contained': { padding: [0.875, 3, 0.875, 3] } }}
                    classes={{ contained: 'MasonButtonError' }}
                    onClick={handleEndSession}
                    variant='contained'
                  >
                    End session
                  </Button>
                </Box>
              </div>
            ) : (
              <Box sx={actionRootSx}>
                <Button onClick={handleLaunch} variant='contained'>
                  Launch Remote-Control
                </Button>
                <Button
                  sx={orientationButtonSx}
                  color='secondary'
                  onClick={handleTakeScreenshot}
                  startIcon={screenCapBusy ? <CircularProgress size={20} /> : <MdScreenshot />}
                  variant='outlined'
                  disabled={screenCapBusy}
                >
                  Take Screenshot
                </Button>
              </Box>
            )}
          </CardActions>
        </Card>
      </Box>
      <div>
        <RemoteControlViewer
          deviceId={deviceId}
          formFactor={formFactor}
          onConnectionInfo={handleConnectionInfo}
          onError={handleError}
          onStart={handleOnStart}
          onStop={handleStop}
          onTimeout={handleTimeout}
          onTimeoutWarning={handleTimeoutWarning}
          orientation={orientation}
          autoRotateOrientation={canvasOrientation}
          start={start}
        />
      </div>

      <DataUsageWarningModal
        feature='remote-control'
        deviceName={deviceName || deviceId}
        onClose={handleDataUsageWarningClose}
        open={showDataUsageModal}
      />
      <TimeoutWarningModal
        onClose={handleTimeoutWarningClose}
        open={timeoutRemainingSec > 0}
        timeoutRemainingSec={timeoutRemainingSec}
      />
      <TimeoutModal onClose={handleTimeoutClose} open={hasTimedOut} />
    </Box>
  );
}
